import {
  AlertBanner,
  Button,
  ButtonIcon,
  Icon,
  InlineMessage,
  Link,
  Typography,
} from "@saviynt/design-system";
import { FormattedMessage, useIntl } from "react-intl";
import { theme } from "../../../constants/theme";
import { useTheme } from "@repo/mfe-core";

interface CFTSectionProps {
  showCftAlert: boolean;
  cftError: string | null;
  onDownload: () => void;
  onCloseAlert: () => void;
  onErrorClear: () => void;
  roleArn?: string;
  externalId?: string;
  isEditMode?: boolean;
  accountId?: string;
}

export function CFTSection({
  showCftAlert,
  cftError,
  onDownload,
  onCloseAlert,
  onErrorClear,
  roleArn: _roleArn,
  externalId: _externalId,
  isEditMode,
  accountId,
}: CFTSectionProps) {
  // roleArn and externalId are intentionally unused but kept for API compatibility
  void _roleArn;
  void _externalId;
  const intl = useIntl();
  const { theme: currentTheme } = useTheme();
  const handleDownload = () => {
    onDownload();
  };

  return (
    <>
      <div className={`flex flex-col ${theme.spacing.fieldGap} mt-6`}>
        <div className="flex items-center gap-2">
          <Typography kind="label">
            <FormattedMessage
              id="zAsYZD"
              defaultMessage="CFT"
              description="CFT label"
            />
          </Typography>
          <Icon
            kind="AlertInfoFilled"
            size="xSmall"
            dataTheme={currentTheme === "dark" ? "dark" : "light"}
          />
        </div>
        <div className="flex gap-2 flex-1 items-center">
          <Button
            kind="outlined"
            size="small"
            leftIcon={<Icon kind="Download" size="xSmall" />}
            onClick={handleDownload}
            dataTheme={currentTheme === "dark" ? "dark" : "light"}
            isDisabled={!accountId}
          >
            <FormattedMessage
              id="mVsBYK"
              defaultMessage="Download CFT File"
              description="Download CFT file button"
            />
          </Button>
          <Link
            href="https://docs.saviyntcloud.com/bundle/ISPM-Guide/page/Content/AWSbedrock-Integration.htm#step_6_create_the_cloudformation_stack_in_aws"
            text={intl.formatMessage({
              id: "ZROK58",
              defaultMessage: "How do I run my CFT file?",
              description: "CFT help link text",
            })}
            color="brand"
            target="_blank"
            dataTheme={currentTheme === "dark" ? "dark" : "light"}
          />
          <Icon
            kind="JumpLink"
            size="xSmall"
            dataTheme={currentTheme === "dark" ? "dark" : "light"}
          />
        </div>
      </div>

      {/* CFT Error Message */}
      {cftError === "AWS_ACCOUNT_ID_REQUIRED" && (
        <div className="flex items-center gap-2">
          <InlineMessage
            text={intl.formatMessage({
              id: "VvnyP5",
              defaultMessage:
                "You need to provide AWS Account ID to Download CFT",
              description:
                "CFT download error message for missing AWS Account ID",
            })}
            colorTheme="critical"
            size="small"
            leftIcon={<Icon kind="AlertCriticalFilled" />}
          />
          <ButtonIcon
            kind="ghost"
            size="small"
            icon={<Icon kind="Close" size="xSmall" />}
            ariaLabel="Close error message"
            onClick={onErrorClear}
          />
        </div>
      )}

      {/* CFT Download Alert Banner - Only show in add mode, not edit mode */}
      {showCftAlert && !isEditMode && (
        <AlertBanner
          colorTheme="Information"
          description={intl.formatMessage({
            id: "5pdsw7",
            defaultMessage:
              "Save this integration as a draft while you complete the CFT setup in AWS. You can resume from the integrations list.",
            description: "CFT download alert description",
          })}
          kind="low"
          title={intl.formatMessage({
            id: "X8hOax",
            defaultMessage: "CFT has been downloaded",
            description: "CFT download alert title",
          })}
          onCancel={onCloseAlert}
          shouldAutoDismiss
          autoDismissDelay={5000}
        />
      )}
    </>
  );
}
